import React from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import LocalActivityOutlinedIcon from "@mui/icons-material/LocalActivityOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { formatMoney } from "/imports/libs/format";

// The offer as one sentence, shared with PackageOfferBanner so the tag and the
// strip say the same thing. The pack's size and price are left to the Plans
// page one tap away — spelling them out here made the nudge read as fine print.
// An `isFrom` offer stands for several packs at once (branches or tiers that
// price apart), so its saving is a ceiling and the copy says so.
export function PackageOfferText({ offer, currency }) {
  return (
    <>
      Purchase a pack and save {offer.isFrom ? "up to " : ""}
      <Box component="span" sx={{ whiteSpace: "nowrap" }}>
        {formatMoney(offer.saving, currency)} per session
      </Box>
    </>
  );
}

/**
 * "A pack makes this cheaper" nudge shown on a bookable session, as a filled
 * tag in the club's colour. Sends the client to the pack on the Plans page —
 * they buy it there, then redeem it at checkout on the way back.
 *
 * Filled rather than coloured text: the club's colour is everywhere on the page
 * (section labels, the chosen day, buttons), and a link drawn in it went
 * unseen. Filled in the accent (secondary) for the same reason — a club
 * without one gets its primary — with the accent's contrastText, like
 * PackageOfferBanner. `compact` (a phone-width card, the options popover) is a full-width
 * strip instead of a pill: a pill squeezed into a phone card's middle column
 * broke into "Save / AED 10/session", which said nothing about a pack.
 */
function PackageOfferHint({ offer, currency, onClick, compact, sx }) {
  if (!offer) return null;

  return (
    <ButtonBase
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      sx={{
        // No outer margin of its own — every surface spaces it the way it
        // spaces its own lines, so the hint keeps their rhythm instead of
        // hanging one notch below it.
        display: compact ? "flex" : "inline-flex",
        width: compact ? "100%" : "auto",
        alignItems: "center",
        gap: compact ? 0.75 : 0.5,
        px: compact ? 1.25 : 1.125,
        py: compact ? 0.75 : 0.5,
        borderRadius: compact ? 2 : 999,
        bgcolor: "secondary.main",
        color: "secondary.contrastText",
        textAlign: "left",
        "&:hover": { bgcolor: "secondary.dark" },
        "&:hover .offer-arrow": { transform: "translateX(0.125rem)" },
        ...sx
      }}
    >
      <LocalActivityOutlinedIcon sx={{ flexShrink: 0, fontSize: "1rem" }} />
      <Typography component="span" sx={{ flex: compact ? 1 : "initial", minWidth: 0, fontWeight: 700, fontSize: "0.75rem", lineHeight: 1.3 }}>
        {compact ? (
          <>
            Save {offer.isFrom ? "up to " : ""}
            <Box component="span" sx={{ whiteSpace: "nowrap" }}>
              {formatMoney(offer.saving, currency)} per session
            </Box>{" "}
            with a pack
          </>
        ) : (
          <PackageOfferText offer={offer} currency={currency} />
        )}
      </Typography>
      <ChevronRightIcon
        className="offer-arrow"
        sx={{ flexShrink: 0, fontSize: "1rem", ml: compact ? 0 : -0.25, transition: "transform 0.15s ease" }}
      />
    </ButtonBase>
  );
}

export default PackageOfferHint;
